/**
 * ERGO 360 Video Scroll Section
 * 视频卡片放大 + 模糊遮罩层 + 自定义光标 + 视频弹窗
 */

/* ========================================
   VideoScrollModal - 视频弹窗 Web Component
   ======================================== */
class VideoScrollModal extends HTMLElement {
  constructor() {
    super();
    this.isOpen = false;
  }

  get overlay() {
    return this.querySelector('.fixed-modal');
  }

  get video() {
    return this.querySelector('.video-scroll-modal__video');
  }

  get closeButton() {
    return this.querySelector('.video-scroll-modal__close');
  }

  connectedCallback() {
    this.setupEventListeners();
  }

  setupEventListeners() {
    if (this.closeButton) {
      this.closeButton.addEventListener('click', () => this.hide());
    }

    if (this.overlay) {
      this.overlay.addEventListener('click', (e) => {
        if (e.target === this.overlay || e.target.classList.contains('video-scroll-modal__content')) {
          this.hide();
        }
      });
    }

    document.addEventListener('keyup', (e) => {
      if (e.code === 'Escape' && this.isOpen) {
        this.hide();
      }
    });

    // GA4 埋点：视频完播
    if (this.video) {
      this.video.addEventListener('ended', () => {
        if (window.Ergo360Analytics) {
          const duration = this.video.duration || 0;
          window.Ergo360Analytics.videoComplete('video_scroll', 'full_video_modal', Math.round(duration));
        }
      });
    }
  }

  show() {
    if (this.isOpen) return;
    this.isOpen = true;

    this.hidden = false;

    requestAnimationFrame(() => {
      if (this.overlay) {
        this.overlay.classList.remove('yd-opacity-0');
        this.overlay.classList.add('yd-opacity-100');
      }
    });

    if (this.video) {
      this.video.currentTime = 0;
      this.video.play().catch(() => {});

      // GA4 埋点：视频播放
      if (window.Ergo360Analytics) {
        window.Ergo360Analytics.videoPlay('video_scroll', 'full_video_modal');
      }
    }

    document.body.classList.add('has-video-modal-open');
    this.dispatchEvent(new CustomEvent('modal:afterShow', { bubbles: true }));
  }

  hide() {
    if (!this.isOpen) return;
    this.isOpen = false;

    if (this.overlay) {
      this.overlay.classList.remove('yd-opacity-100');
      this.overlay.classList.add('yd-opacity-0');
    }

    if (this.video) {
      this.video.pause();
    }

    setTimeout(() => {
      this.hidden = true;
      document.body.classList.remove('has-video-modal-open');
      this.dispatchEvent(new CustomEvent('modal:afterHide', { bubbles: true }));
    }, 300);
  }
}

if (!customElements.get('video-scroll-modal')) {
  customElements.define('video-scroll-modal', VideoScrollModal);
}

/* ========================================
   Ergo360VideoScroll - 主控制器
   ======================================== */
class Ergo360VideoScroll {
  constructor(container) {
    this.container = container;
    this.sectionId = container.dataset.sectionId;

    // 元素引用
    this.videoSection = container.querySelector('.video-scroll__video-section');
    this.titleWrapper = container.querySelector('.video-scroll__title-wrapper');
    this.videoWrapper = container.querySelector('.video-scroll__video-wrapper');
    this.videoCard = container.querySelector('.video-scroll__video-card');
    this.videoDesktop = container.querySelector('.video-scroll__video--desktop');
    this.videoMobile = container.querySelector('.video-scroll__video--mobile');
    this.video = null; // 在 init 中根据设备类型设置
    this.cursor = container.querySelector('.video-scroll__cursor');
    this.mobilePlayBtn = container.querySelector('.video-scroll__mobile-play-btn');
    this.modal = document.querySelector(`#VideoScrollModal-${this.sectionId}`);

    // PC 端新增元素
    this.blurOverlay = container.querySelector('.video-scroll__blur-overlay');
    this.maskDesktop = container.querySelector('.video-scroll__mask-section--desktop');
    this.initialHint = container.querySelector('.video-scroll__initial-hint');

    // 移动端新增元素
    this.blurOverlayMobile = container.querySelector('.video-scroll__blur-overlay--mobile');
    this.maskMobileScroll = container.querySelector('.video-scroll__mask-section--mobile-scroll');

    // 状态
    this.scaleTimeline = null;
    this.pinTimeline = null;
    this.mobilePinTimeline = null;
    this.scrollTriggerInstances = [];
    this.isMobile = window.innerWidth < 768;

    this.init();
  }

  init() {
    // 根据设备类型设置当前视频引用
    this.video = this.isMobile ? this.videoMobile : this.videoDesktop;

    // 只为当前设备的视频加载 src（懒加载，避免双端同时加载）
    if (this.video && this.video.dataset.src) {
      this.video.src = this.video.dataset.src;
      this.video.load();
    }

    // 设置视频宽高比为浏览器视口比例
    this.setViewportRatio();

    this.waitForGSAP().then(() => {
      if (!this.isMobile) {
        this.initScrollAnimation();
        this.initCursor();
      } else {
        this.initMobileAnimation();
      }
      this.initVideoClick();
    });

    // 视口可见性控制
    this.setupVisibilityObserver();

    window.addEventListener('resize', this.onResize.bind(this));
  }

  /**
   * 设置视口可见性监听
   * 进入视口播放，离开视口暂停
   */
  setupVisibilityObserver() {
    if (!this.video) return;

    this.visibilityObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            this.video.play().catch(() => {});
          } else {
            this.video.pause();
          }
        });
      },
      { threshold: 0.1 }
    );

    this.visibilityObserver.observe(this.container);
  }

  setViewportRatio() {
    const ratio = window.innerWidth / window.innerHeight;
    this.container.style.setProperty('--viewport-ratio', ratio);
  }

  waitForGSAP() {
    return new Promise((resolve) => {
      const check = setInterval(() => {
        if (typeof gsap !== 'undefined' && typeof ScrollTrigger !== 'undefined') {
          clearInterval(check);
          gsap.registerPlugin(ScrollTrigger);
          resolve();
        }
      }, 50);

      setTimeout(() => {
        clearInterval(check);
        resolve();
      }, 5000);
    });
  }

  /**
   * 初始化 ScrollTrigger 动画（桌面端）
   * 阶段1: 视频从 80vw 放大到 100vw（进入时）
   * 阶段2: 视频容器 Pin 住 + 空滚动 400px + 遮罩淡入 + Mask 层滑入
   */
  initScrollAnimation() {
    if (typeof gsap === 'undefined' || typeof ScrollTrigger === 'undefined') {
      // GSAP or ScrollTrigger not loaded
      return;
    }

    // 视频从 80vw 放大到 100vw，计算 scale 值
    const targetScale = 100 / 80; // 1.25

    // ========================================
    // 阶段1: 视频放大动画（进入时 80vw → 100vw）
    // ========================================
    this.scaleTimeline = gsap.timeline({
      scrollTrigger: {
        trigger: this.videoSection,
        start: 'top bottom', // 视频容器顶边进入视窗底边时开始
        end: 'top top',      // 视频容器顶边到达视窗顶边时结束
        scrub: 1
      }
    });

    this.scaleTimeline.to(this.videoCard, {
      scale: targetScale,
      borderRadius: 0,
      duration: 1,
      ease: 'none'
    }, 0);

    // 初始提示文字淡入（视频放大后半段淡入）
    if (this.initialHint) {
      this.scaleTimeline.to(this.initialHint, {
        opacity: 1,
        duration: 0.5,
        ease: 'none'
      }, 0.5);
    }

    this.scrollTriggerInstances.push(this.scaleTimeline.scrollTrigger);

    // ========================================
    // 阶段2: Pin + 遮罩淡入 + Mask 层滑入 + 短暂离场
    // ========================================
    // 滚动距离：400px 空滚动 + 100vh Mask 进入 + 200px 离场
    const emptyScrollDistance = 400;
    const maskEnterDistance = window.innerHeight;
    const maskExitDistance = 200;
    const totalPinDistance = emptyScrollDistance + maskEnterDistance + maskExitDistance;

    // 设置 Mask 层初始状态（在屏幕下方）
    if (this.maskDesktop) {
      gsap.set(this.maskDesktop, { yPercent: 100 });
    }

    this.pinTimeline = gsap.timeline({
      scrollTrigger: {
        trigger: this.videoSection,
        start: 'top top',                    // 视频容器顶边到达视窗顶边时开始 Pin
        end: `+=${totalPinDistance}`,        // Pin 总距离：400px + 100vh + 400px
        pin: true,
        scrub: 1,
        pinSpacing: true
      }
    });

    // 计算动画时间点
    const maskEnterStart = emptyScrollDistance / totalPinDistance;           // Mask 进入开始
    const maskEnterEnd = (emptyScrollDistance + maskEnterDistance) / totalPinDistance; // Mask 进入结束
    // maskEnterEnd 到 1.0 是 Mask 离场阶段

    // 初始提示淡出（mask 进入前淡出）
    if (this.initialHint) {
      this.pinTimeline.to(this.initialHint, {
        opacity: 0,
        duration: maskEnterStart * 0.5,
        ease: 'none'
      }, 0);
    }

    // 遮罩淡入（与 Mask 进入同时开始，离场时保持不淡出）
    if (this.blurOverlay) {
      this.pinTimeline.to(this.blurOverlay, {
        opacity: 1,
        duration: maskEnterEnd - maskEnterStart,
        ease: 'none'
      }, maskEnterStart);
    }

    // Mask 层从底部滑入（入场完成后停留，然后整个 section 一起上滑离场）
    if (this.maskDesktop) {
      this.pinTimeline.to(this.maskDesktop, {
        yPercent: 0,
        duration: maskEnterEnd - maskEnterStart,
        ease: 'none'
      }, maskEnterStart);
    }

    this.scrollTriggerInstances.push(this.pinTimeline.scrollTrigger);
  }

  /**
   * 初始化移动端 ScrollTrigger 动画
   * 移动端：Pin + 遮罩淡入 + Mask 层进场 + 短暂离场（无视频放大）
   */
  initMobileAnimation() {
    if (typeof gsap === 'undefined' || typeof ScrollTrigger === 'undefined') {
      // GSAP or ScrollTrigger not loaded
      return;
    }

    if (!this.blurOverlayMobile || !this.maskMobileScroll) return;

    // 设置 Mask 层初始状态（在屏幕下方）
    gsap.set(this.maskMobileScroll, { yPercent: 100 });

    // 滚动距离计算：200px 空滚动 + 100vh Mask 进入 + 30vh 离场
    const emptyScrollDistance = 200;
    const maskEnterDistance = window.innerHeight;
    const maskExitDistance = window.innerHeight * 0.3;
    const totalPinDistance = emptyScrollDistance + maskEnterDistance + maskExitDistance;

    this.mobilePinTimeline = gsap.timeline({
      scrollTrigger: {
        trigger: this.videoSection,
        start: 'top top',
        end: `+=${totalPinDistance}`,
        pin: true,
        scrub: 1,
        pinSpacing: true
      }
    });

    // 计算动画时间点
    const maskEnterStart = emptyScrollDistance / totalPinDistance;
    const maskEnterEnd = (emptyScrollDistance + maskEnterDistance) / totalPinDistance;
    // maskEnterEnd 到 1.0 是 Mask 离场阶段

    // Watch Full Video 按钮淡出（空滚动阶段）
    if (this.mobilePlayBtn) {
      this.mobilePinTimeline.to(this.mobilePlayBtn, {
        opacity: 0,
        duration: maskEnterStart,
        ease: 'none'
      }, 0);
    }

    // 遮罩淡入
    this.mobilePinTimeline.to(this.blurOverlayMobile, {
      opacity: 1,
      duration: maskEnterEnd - maskEnterStart,
      ease: 'none'
    }, maskEnterStart);

    // Mask 层从底部滑入（入场完成后停留，然后整个 section 一起上滑离场）
    this.mobilePinTimeline.to(this.maskMobileScroll, {
      yPercent: 0,
      duration: maskEnterEnd - maskEnterStart,
      ease: 'none'
    }, maskEnterStart);

    this.scrollTriggerInstances.push(this.mobilePinTimeline.scrollTrigger);
  }

  initCursor() {
    if (!this.cursor || !this.videoCard) return;

    const isTouch = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
    if (isTouch) {
      this.cursor.style.display = 'none';
      return;
    }

    // 光标尺寸（用于居中计算）
    const cursorSize = 140;
    const cursorOffset = cursorSize / 2;

    // 当前光标位置
    let currentX = 0;
    let currentY = 0;
    let targetX = 0;
    let targetY = 0;
    let isHovering = false;
    let animationId = null;

    // 平滑动画函数
    const animate = () => {
      if (!isHovering) {
        animationId = null;
        return;
      }

      // 平滑跟随（缓动系数）
      currentX += (targetX - currentX) * 0.15;
      currentY += (targetY - currentY) * 0.15;

      // 应用位置（减去一半尺寸使光标居中于鼠标位置）
      this.cursor.style.transform = `translate(${currentX - cursorOffset}px, ${currentY - cursorOffset}px)`;

      animationId = requestAnimationFrame(animate);
    };

    // 鼠标移动事件
    document.addEventListener('mousemove', (e) => {
      targetX = e.clientX;
      targetY = e.clientY;
    });

    // 鼠标进入视频区域
    this.videoCard.addEventListener('mouseenter', (e) => {
      isHovering = true;
      // 立即设置初始位置
      currentX = e.clientX;
      currentY = e.clientY;
      targetX = e.clientX;
      targetY = e.clientY;
      this.cursor.style.transform = `translate(${currentX - cursorOffset}px, ${currentY - cursorOffset}px)`;
      this.cursor.style.opacity = '1';
      document.body.style.cursor = 'none';

      // 开始动画
      if (!animationId) {
        animate();
      }
    });

    // 鼠标离开视频区域
    this.videoCard.addEventListener('mouseleave', () => {
      isHovering = false;
      this.cursor.style.opacity = '0';
      document.body.style.cursor = '';
    });

    // 点击效果
    this.videoCard.addEventListener('mousedown', () => {
      this.cursor.classList.add('video-scroll__cursor--clicking');
    });

    this.videoCard.addEventListener('mouseup', () => {
      this.cursor.classList.remove('video-scroll__cursor--clicking');
    });
  }

  initVideoClick() {
    if (!this.modal) return;

    // 桌面端：点击视频卡片打开弹窗
    if (this.videoCard) {
      this.videoCard.addEventListener('click', () => {
        this.openModal();
      });
    }

    // 移动端：点击 Watch Full Video 按钮打开弹窗
    if (this.mobilePlayBtn) {
      this.mobilePlayBtn.addEventListener('click', () => {
        this.openModal();
      });
    }
  }

  openModal() {
    if (!this.modal) return;

    if (this.video) {
      this.video.pause();
    }

    if (this.cursor) {
      this.cursor.style.opacity = '0';
    }
    document.body.style.cursor = '';

    // GA4 埋点：视频弹窗打开
    if (window.Ergo360Analytics) {
      const source = this.isMobile ? 'mobile_btn' : 'cursor';
      window.Ergo360Analytics.videoModalOpen(source);
    }

    this.modal.show();

    this.modal.addEventListener('modal:afterHide', () => {
      if (this.video) {
        this.video.play().catch(() => {});
      }
    }, { once: true });
  }

  onResize() {
    const wasMobile = this.isMobile;
    this.isMobile = window.innerWidth < 768;

    // 更新视口比例
    this.setViewportRatio();

    if (wasMobile !== this.isMobile) {
      this.destroy();
      this.init();
    }
  }

  destroy() {
    // 销毁所有 ScrollTrigger 实例
    this.scrollTriggerInstances.forEach(instance => {
      if (instance) {
        instance.kill();
      }
    });
    this.scrollTriggerInstances = [];

    // 销毁 timeline
    if (this.scaleTimeline) {
      this.scaleTimeline.kill();
      this.scaleTimeline = null;
    }

    if (this.pinTimeline) {
      this.pinTimeline.kill();
      this.pinTimeline = null;
    }

    if (this.mobilePinTimeline) {
      this.mobilePinTimeline.kill();
      this.mobilePinTimeline = null;
    }

    // 重置元素状态 - PC 端
    if (this.blurOverlay) {
      gsap.set(this.blurOverlay, { opacity: 0 });
    }

    if (this.maskDesktop) {
      gsap.set(this.maskDesktop, { yPercent: 100 });
    }

    if (this.videoCard) {
      gsap.set(this.videoCard, { scale: 1, borderRadius: 16 });
    }

    if (this.initialHint) {
      gsap.set(this.initialHint, { opacity: 0 });
    }

    // 重置元素状态 - 移动端
    if (this.blurOverlayMobile) {
      gsap.set(this.blurOverlayMobile, { opacity: 0 });
    }

    if (this.maskMobileScroll) {
      gsap.set(this.maskMobileScroll, { yPercent: 100 });
    }

    if (this.mobilePlayBtn) {
      gsap.set(this.mobilePlayBtn, { opacity: 1 });
    }

    if (this.visibilityObserver) {
      this.visibilityObserver.disconnect();
    }

    window.removeEventListener('resize', this.onResize);
  }
}

/* ========================================
   初始化
   ======================================== */
document.addEventListener('DOMContentLoaded', () => {
  const containers = document.querySelectorAll('.ergo-360-video-scroll');

  containers.forEach((container) => {
    new Ergo360VideoScroll(container);
  });
});

/* ========================================
   Shopify 主题编辑器支持
   ======================================== */
if (typeof Shopify !== 'undefined' && Shopify.designMode) {
  document.addEventListener('shopify:section:load', (event) => {
    const container = event.target.querySelector('.ergo-360-video-scroll');
    if (container) {
      new Ergo360VideoScroll(container);
    }
  });

  document.addEventListener('shopify:section:unload', (event) => {
    const container = event.target.querySelector('.ergo-360-video-scroll');
    if (container && container._ergo360VideoScroll) {
      container._ergo360VideoScroll.destroy();
    }
  });
}
